import { initiativeWriteSchema, type Finding, type InitiativeWriteInput } from '@complan/contracts';
import type { ResolvableFinding } from '@/shared/i18n/resolve-finding';

/**
 * The pure model behind the validation summary and its submit gate (task 23.2).
 *
 * ## Two authorities, one finding set (R5.7, R8.12)
 *
 * The server's Validation_Engine is authoritative: it returns the *complete*
 * finding set of all four tiers in one pass (never a prefix), and its findings
 * carry the effective tier after configuration (R8.9). The client additionally
 * runs the **syntactic hard rules** from the shared `@complan/contracts` Zod
 * schema so the user sees an obvious mistake (an over-long title, an inverted
 * date range, a broken split) the instant they commit a field, without waiting
 * for the 300 ms-debounced round trip. Those client findings are advisory: when
 * the server answers, its findings for the same rule replace the client's, so the
 * summary can never show a client guess the server has since contradicted.
 *
 * This module owns the tier ordering, the per-tier presentation tokens (UI spec
 * §3.8), the client-side syntactic evaluation, the merge of client and server
 * findings, and the gate predicates the submit button reads (UI spec §6.3). It
 * holds no React and no catalogue text — the tier glyphs are the non-letter
 * symbols §3.8 fixes, and every label resolves through the catalogue at the
 * component call site.
 */

/** The four validation tiers, in the stacking order UI spec §6.3 fixes. */
export const TIER_ORDER = ['hard', 'governance', 'warning', 'information'] as const;

/** One of the four tiers the summary stacks. */
export type SummaryTier = (typeof TIER_ORDER)[number];

/**
 * The presentation tokens of each tier (UI spec §3.8): the alert class, its exact
 * colours, and the glyph prefix. Colours are the literal hex values §3.8 pins;
 * they are style values, not user-facing text, so they live here rather than in
 * the catalogue. The glyph is a non-letter symbol §3.8 fixes.
 */
export interface TierPresentation {
  readonly tier: SummaryTier;
  /** The `a-*` class name of UI spec §3.8. */
  readonly className: string;
  readonly background: string;
  readonly border: string;
  readonly text: string;
  /** The glyph prefix (`✕`/`!`/`⚠`/`ℹ`); a non-letter symbol, not catalogue text. */
  readonly glyph: string;
}

/** The four tiers' presentation, keyed by tier (UI spec §3.8). */
export const TIER_PRESENTATION: Readonly<Record<SummaryTier, TierPresentation>> = {
  hard: {
    tier: 'hard',
    className: 'a-hard',
    background: '#FCEDEB',
    border: '#F0C3BC',
    text: '#8E2A1E',
    glyph: '✕',
  },
  governance: {
    tier: 'governance',
    className: 'a-gov',
    background: '#FDF4E4',
    border: '#F0DBB4',
    text: '#7D5300',
    glyph: '!',
  },
  warning: {
    tier: 'warning',
    className: 'a-warn',
    background: '#FFF8E8',
    border: '#F2E2BC',
    text: '#7D5300',
    glyph: '⚠',
  },
  information: {
    tier: 'information',
    className: 'a-info',
    background: '#EDF4FB',
    border: '#CFE2F5',
    text: '#14538F',
    glyph: 'ℹ',
  },
};

/**
 * A finding as the summary consumes it. It is the contract {@link Finding} plus
 * the {@link ResolvableFinding} shape the i18n resolver reads, and a `source`
 * marking whether it came from the authoritative server evaluation or the
 * client-side syntactic pre-check.
 */
export interface SummaryFinding extends ResolvableFinding {
  readonly ruleId: string;
  readonly tier: SummaryTier;
  readonly fields: readonly string[];
  readonly source: 'server' | 'client';
}

/**
 * Adapt a contract {@link Finding} (server-authoritative) to a
 * {@link SummaryFinding}. The contract carries the administrator override on
 * `message`; the resolver reads it as `messageOverride`, so the field is mapped
 * across here.
 */
export function fromServerFinding(finding: Finding): SummaryFinding {
  return {
    ruleId: finding.ruleId,
    tier: finding.tier,
    messageKey: finding.messageKey,
    messageOverride: finding.message,
    detail: finding.detail ?? undefined,
    fields: finding.fields,
    source: 'server',
  };
}

/**
 * The client-side syntactic hard rules, evaluated from the shared
 * {@link initiativeWriteSchema} for immediate feedback (R5.7, R8.3).
 *
 * These mirror the syntactic subset of the six hard rules of R8.3 that a Zod
 * shape can decide on its own — field length, presence and range — so a user sees
 * the mistake as they type rather than on the next server round trip. They are
 * *not* the authority: the server evaluates the same conditions and its finding
 * for the rule supersedes the client one on merge.
 *
 * Each maps a schema issue to the same `messageKey` the server would emit, so a
 * client finding and its later server replacement resolve to identical text.
 */

/** The Title field schema, reused from the write shape for the length check. */
const titleFieldSchema = initiativeWriteSchema.shape.title;

/** Map each syntactic check to the catalogue key the server also emits for it. */
const CLIENT_HARD_RULES: readonly {
  readonly ruleId: string;
  readonly messageKey: string;
  readonly fields: readonly string[];
  readonly evaluate: (form: Partial<InitiativeWriteInput>) => boolean;
}[] = [
  {
    // R8.3: a Title that is empty or exceeds 200 characters.
    ruleId: 'title.length',
    messageKey: 'validation.title.length',
    fields: ['title'],
    evaluate: (form) => {
      const title = form.title;
      if (typeof title !== 'string' || title.length === 0) return false;
      return !titleFieldSchema.safeParse(title).success;
    },
  },
  {
    // R8.3: a missing Owner.
    ruleId: 'owner.missing',
    messageKey: 'validation.owner.missing',
    fields: ['ownerUserId'],
    evaluate: (form) => typeof form.ownerUserId !== 'string' || form.ownerUserId.length === 0,
  },
  {
    // R8.3: an end date earlier than the start date. Only decidable once both are
    // present; a blank field is a completeness gap the server reports, not a range
    // error the client should guess.
    ruleId: 'date_range.end_before_start',
    messageKey: 'validation.dateRange.endBeforeStart',
    fields: ['endDate'],
    evaluate: (form) => {
      const { startDate, endDate } = form;
      if (typeof startDate !== 'string' || typeof endDate !== 'string') return false;
      if (startDate.length === 0 || endDate.length === 0) return false;
      return endDate < startDate;
    },
  },
];

/**
 * Evaluate the client-side syntactic hard rules against the current form values.
 * Returns a {@link SummaryFinding} per triggered rule, each tagged `client`.
 */
export function evaluateClientHardRules(
  form: Partial<InitiativeWriteInput>,
): readonly SummaryFinding[] {
  const findings: SummaryFinding[] = [];
  for (const rule of CLIENT_HARD_RULES) {
    if (rule.evaluate(form)) {
      findings.push({
        ruleId: rule.ruleId,
        tier: 'hard',
        messageKey: rule.messageKey,
        messageOverride: null,
        detail: undefined,
        fields: rule.fields,
        source: 'client',
      });
    }
  }
  return findings;
}

/**
 * Merge the authoritative server findings with the client syntactic findings.
 *
 * The server evaluation is authoritative *and complete*: it returns every active
 * rule of all four tiers in one pass, never a prefix (R8.12). So once the server
 * has evaluated (`serverEvaluated` true) its finding set stands alone — a client
 * syntactic finding is only a stand-in for the round trip and must never survive
 * a server answer that did not report it, or the summary could show a client
 * guess the server has since contradicted. Before the first server answer the
 * client findings stand alone, so the summary is never empty while the user waits
 * out the 300 ms debounce.
 */
export function mergeFindings(
  serverFindings: readonly SummaryFinding[],
  clientFindings: readonly SummaryFinding[],
  serverEvaluated: boolean,
): readonly SummaryFinding[] {
  return serverEvaluated ? [...serverFindings] : [...clientFindings];
}

/**
 * A stable identity for one finding: the rule, the sentence it resolves to, the
 * fields it affects and the detail that distinguishes it from its siblings.
 *
 * The detail is part of the identity, and that is the whole point. A rule may
 * legitimately report **several findings of the same rule in one evaluation** —
 * `WV-001` emits one per overlapping Initiative, each naming a different
 * counterpart in its detail — so an identity that ignored the detail would treat
 * four genuinely different overlaps as one and hide three of them. Conversely an
 * identity of rule alone cannot tell a real repeat from a distinct occurrence.
 *
 * Detail keys are sorted so two equal details produce one identity regardless of
 * the key order the server happened to serialise them in.
 */
export function findingIdentity(finding: SummaryFinding): string {
  const detail = finding.detail;
  const detailKey =
    detail === undefined || detail === null
      ? ''
      : JSON.stringify(
          Object.keys(detail)
            .sort()
            .map((key) => [key, detail[key]]),
        );
  return [
    finding.ruleId,
    finding.messageKey,
    finding.messageOverride ?? '',
    [...finding.fields].join(','),
    detailKey,
  ].join('|');
}

/**
 * Collapse findings that are the same finding twice, keeping the first of each.
 *
 * Two findings collapse only when they are identical down to their detail (see
 * {@link findingIdentity}), so distinct occurrences of one rule — four different
 * overlapping Initiatives — all survive. What this removes is the genuine repeat:
 * the same unmet rule arriving from two sources in one list, which reads to the
 * user as two problems when it is one.
 */
export function dedupeFindings(
  findings: readonly SummaryFinding[],
): readonly SummaryFinding[] {
  const seen = new Set<string>();
  const unique: SummaryFinding[] = [];
  for (const finding of findings) {
    const identity = findingIdentity(finding);
    if (seen.has(identity)) continue;
    seen.add(identity);
    unique.push(finding);
  }
  return unique;
}

/** The findings of one tier, in the order they arrived. */
export function findingsOfTier(
  findings: readonly SummaryFinding[],
  tier: SummaryTier,
): readonly SummaryFinding[] {
  return findings.filter((finding) => finding.tier === tier);
}

/**
 * The findings grouped by tier and returned in the fixed stacking order
 * hard → governance → warning → information (UI spec §6.3). Empty tiers are
 * omitted so the summary renders only the tiers that have findings, but the
 * order of those that remain is always the canonical one.
 */
export function stackByTier(
  findings: readonly SummaryFinding[],
): readonly { readonly tier: SummaryTier; readonly findings: readonly SummaryFinding[] }[] {
  return TIER_ORDER.map((tier) => ({ tier, findings: findingsOfTier(findings, tier) })).filter(
    (group) => group.findings.length > 0,
  );
}

/**
 * Whether any hard finding is present. A hard finding blocks the save and every
 * transition (R8.2), which is what gives `Submit for review` its blocked
 * treatment.
 */
export function hasHardFinding(findings: readonly SummaryFinding[]): boolean {
  return findings.some((finding) => finding.tier === 'hard');
}

/**
 * Whether the submit (a transition to Submitted) is currently blocked (UI spec
 * §6.3, R8.2, R8.4, R8.5).
 *
 * A submission is blocked while:
 * - any hard finding stands (R8.2), or
 * - any governance finding stands (R8.4 blocks the transition to Submitted), or
 * - any warning of this evaluation is not yet acknowledged (R8.5).
 *
 * The button stays **clickable** regardless — clicking a blocked submit surfaces
 * the reason rather than doing nothing (task 23.2) — so this predicate drives the
 * blocked *styling* and the surfaced reason, not the button's `disabled` state.
 */
export function isSubmitBlocked(
  findings: readonly SummaryFinding[],
  unacknowledgedWarningRuleIds: readonly string[],
): boolean {
  const hasBlockingTier = findings.some(
    (finding) => finding.tier === 'hard' || finding.tier === 'governance',
  );
  return hasBlockingTier || unacknowledgedWarningRuleIds.length > 0;
}

/**
 * The catalogue key naming why a blocked submit cannot proceed, surfaced when the
 * user clicks the blocked button (task 23.2). Ordered by severity: a hard block
 * is named before a governance block before an unacknowledged warning.
 */
export function submitBlockedReasonKey(
  findings: readonly SummaryFinding[],
  unacknowledgedWarningRuleIds: readonly string[],
): string | null {
  if (findings.some((finding) => finding.tier === 'hard')) {
    return 'validationSummary.blockedReason.hard';
  }
  if (findings.some((finding) => finding.tier === 'governance')) {
    return 'validationSummary.blockedReason.governance';
  }
  if (unacknowledgedWarningRuleIds.length > 0) {
    return 'validationSummary.blockedReason.warning';
  }
  return null;
}
